import { AuthenticationDetails, CognitoRefreshToken, CognitoUser, CognitoUserPool } from "amazon-cognito-identity-js";
import { jwtDecode } from 'jwt-decode';

export interface User {
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

const poolData = {
  UserPoolId: import.meta.env.VITE_USER_POOL_ID || '',
  ClientId: import.meta.env.VITE_CLIENT_ID || '',
};

if (!poolData.UserPoolId || !poolData.ClientId) {
  console.error('Cognito configuration missing!');
  console.error('VITE_USER_POOL_ID:', import.meta.env.VITE_USER_POOL_ID);
  console.error('VITE_CLIENT_ID:', import.meta.env.VITE_CLIENT_ID);
}

const userPool = new CognitoUserPool(poolData);

export const signIn = (username: string, password: string): Promise<{ 
  user: User | null; 
  newPasswordRequired: boolean; 
  token: string; 
  refreshToken: string 
}> => {
  const user = new CognitoUser({ Username: username, Pool: userPool });
  const authenticationDetails = new AuthenticationDetails({ Username: username, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(authenticationDetails, {
      onSuccess: result => {
        const idToken = result.getIdToken().getJwtToken();
        const refreshToken = result.getRefreshToken().getToken();
        
        // Decode JWT to get user info
        try {
          const decodedToken: any = jwtDecode(idToken);
          
          const userData: User = {
            username: username,
            email: decodedToken.email,
            firstName: decodedToken.given_name,
            lastName: decodedToken.family_name,
          };
          
          resolve({ 
            user: userData, 
            newPasswordRequired: false, 
            token: idToken,
            refreshToken: refreshToken
          });
        } catch (error) {
          console.error('Error decoding JWT:', error);
          resolve({ 
            user: { username }, 
            newPasswordRequired: false, 
            token: idToken,
            refreshToken: refreshToken
          });
        }
      },
      onFailure: err => {
        console.error('Authentication failed:', err);
        reject(err);
      },
      newPasswordRequired: (_userAttributes, requiredAttributes) => {
        // Handle new password requirement
        user.completeNewPasswordChallenge(password, requiredAttributes, {
          onSuccess: result => {
            const idToken = result.getIdToken().getJwtToken();
            const refreshToken = result.getRefreshToken().getToken();
            
            resolve({ 
              user: { username }, 
              newPasswordRequired: false, 
              token: idToken,
              refreshToken: refreshToken
            });
          },
          onFailure: err => reject(err),
          mfaRequired: () => {
            console.error("MFA is required!");
          }
        });
      }
    });
  });
};

export const refreshAccessToken = async (refreshToken: string): Promise<string> => {
  const refresh = new CognitoRefreshToken({ RefreshToken: refreshToken });
  const user = new CognitoUser({ Username: "username", Pool: userPool });

  return new Promise((resolve, reject) => {
    user.refreshSession(refresh, (err, session) => {
      if (err) {
        reject(err);
      } else {
        const idToken = session.getIdToken().getJwtToken();
        resolve(idToken);
      }
    });
  });
};

export const changePassword = (username: string, oldPassword: string, newPassword: string): Promise<any> => {
  const user = new CognitoUser({ Username: username, Pool: userPool });

  return new Promise((resolve, reject) => {
    user.changePassword(oldPassword, newPassword, function(err, result) {
      if (err) {
        reject(err);
      } else {
        resolve(result);
      }
    });
  });
};

export const signOut = (): Promise<void> => {
  return new Promise((resolve) => {
    const user = userPool.getCurrentUser();
    if (user) {
      user.signOut();
    }
    resolve();
  });
};
